import React, { useEffect, useState } from 'react';
import { collection, query, where, orderBy, getDocs, doc, updateDoc, deleteDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { writeAuditLog } from '../utils/auditLog';
import { Check, Package, Truck, NotePencil, Eye, EyeSlash, Trash } from '@phosphor-icons/react';

function StatCard({ value, label, sub, color, selected, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: '#fff', border: selected ? `2px solid ${color}` : '1px solid #E2E8F0',
      borderRadius: 12, padding: '20px 24px', cursor: onClick ? 'pointer' : 'default',
      flex: 1, minWidth: 140, boxShadow: selected ? `0 0 0 3px ${color}22` : 'none',
      transition: 'all 0.15s'
    }}>
      <div style={{ fontSize: 32, fontWeight: 800, color: color }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
function ActionBtn({ icon, title, color, bg, onClick }) {
  return (
    <button onClick={onClick} title={title} style={{
      width: 30, height: 30, borderRadius: 7, border: 'none',
      background: bg || '#F1F5F9', cursor: 'pointer', color: color || '#64748B',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      transition: 'background 0.12s',
    }}
    onMouseEnter={e => e.currentTarget.style.background = bg ? bg + 'dd' : '#E2E8F0'}
    onMouseLeave={e => e.currentTarget.style.background = bg || '#F1F5F9'}
    >{icon}</button>
  );
}

export default function DashboardPage() {
  const { pharmacy, role, user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [noteModal, setNoteModal] = useState(null); // { id, name, note }
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name }
  const [filter, setFilter] = useState('overdue');
  const [localCycles, setLocalCycles] = useState({});
  const [localDates, setLocalDates] = useState({});

  const canEdit = role === 'admin' || role === 'pharmacist' || role === 'superadmin' || role === 'technician';

  const load = async () => {
    if (!pharmacy) return;
    setLoading(true);
    const snap = await getDocs(
      query(collection(db, 'pharmacies', pharmacy.id, 'patients'),
        where('status', '==', 'active'))
    );
    const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setPatients(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [pharmacy]);

  const now = new Date();
  const toDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (v.toDate) return v.toDate();
    return new Date(v);
  };

  const active = patients.filter(p => !p.ignored);
  const ready = active.filter(p => p.ready);
  const overdue = active.filter(p => {
    const d = toDate(p.nextDue);
    return d && d < now && !p.ready;
  });
  const dueThisWeek = active.filter(p => {
    const d = toDate(p.nextDue);
    const week = new Date(now); week.setDate(week.getDate() + 7);
    return d && d >= now && d <= week;
  });
  const dueIn3 = active.filter(p => {
    const d = toDate(p.nextDue);
    const three = new Date(now); three.setDate(three.getDate() + 3);
    return d && d >= now && d <= three;
  });

  const displayed = filter === 'overdue' ? overdue
    : filter === 'ready' ? ready
    : filter === 'week' ? dueThisWeek
    : filter === '3days' ? dueIn3
    : active;

  const fmtDate = (v) => {
    const d = toDate(v);
    return d ? d.toISOString().slice(0, 10) : '—';
  };

  const markReady = async (p) => {
    await updateDoc(doc(db, 'pharmacies', pharmacy.id, 'patients', p.id), {
      ready: true, updatedAt: serverTimestamp(),
      updatedBy: user.uid,
      updatedByName: user.email,
      updatedByRole: role,
    });
    await writeAuditLog(pharmacy.id, user, {
      action: 'Patient marked ready',
      entityType: 'patient',
      entityId: p.id,
      entityName: p.name,
      previousValue: String(p.ready ?? false),
      newValue: 'true',
      actorRole: role || '',
      source: 'client',
    });
    load();
  };

  const saveField = async (p, newCycle, newPickup) => {
    const ref    = doc(db, 'pharmacies', pharmacy.id, 'patients', p.id);
    const cycle  = parseInt(newCycle) || p.cycleLength || 28;
    const lpDate = newPickup ? new Date(newPickup) : null;
    const lp     = lpDate ? Timestamp.fromDate(lpDate) : p.lastPickup;
    let nd = p.nextDue;
    if (lpDate) {
      const next = new Date(lpDate);
      next.setDate(next.getDate() + cycle);
      nd = Timestamp.fromDate(next);
    }
    await updateDoc(ref, {
      cycleLength: cycle, lastPickup: lp, nextDue: nd,
      updatedAt: serverTimestamp(), updatedBy: user.uid,
      updatedByName: user.email, updatedByRole: role,
    });
    await writeAuditLog(pharmacy.id, user, {
      action: 'Patient updated', entityType: 'patient',
      entityId: p.id, entityName: p.name,
      previousValue: '', newValue: '',
      actorRole: role || '', source: 'client',
    });
    load();
  };

  const saveNote = async () => {
    if (!noteModal) return;
    await updateDoc(doc(db, 'pharmacies', pharmacy.id, 'patients', noteModal.id), {
      note: noteModal.note, updatedAt: serverTimestamp(),
      updatedBy: user.uid, updatedByName: user.email, updatedByRole: role,
    });
    await writeAuditLog(pharmacy.id, user, {
      action: 'Patient note updated', entityType: 'patient',
      entityId: noteModal.id, entityName: noteModal.name,
      previousValue: '', newValue: noteModal.note,
      actorRole: role || '', source: 'client',
    });
    setNoteModal(null);
    load();
  };

  const markPickedUp = async (p) => {
    const today = Timestamp.fromDate(new Date());
    const next = new Date(); next.setDate(next.getDate() + (p.cycleLength || 28));
    await updateDoc(doc(db, 'pharmacies', pharmacy.id, 'patients', p.id), {
      lastPickup: today, nextDue: Timestamp.fromDate(next),
      ready: false, updatedAt: serverTimestamp(),
      updatedBy: user.uid,
      updatedByName: user.email,
      updatedByRole: role,
    });
    await writeAuditLog(pharmacy.id, user, {
      action: 'Patient marked picked up',
      entityType: 'patient',
      entityId: p.id,
      entityName: p.name,
      previousValue: p.lastPickup?.toDate?.()?.toISOString?.() || '',
      newValue: today.toDate().toISOString(),
      actorRole: role || '',
      source: 'client',
    });
    load();
  };

  const toggleDelivery = async (p) => {
    const newVal = !p.deliveryPatient;
    await updateDoc(doc(db, 'pharmacies', pharmacy.id, 'patients', p.id), {
      deliveryPatient: newVal, updatedAt: serverTimestamp(),
      updatedBy: user.uid, updatedByName: user.email, updatedByRole: role,
    });
    load();
  };

  const ignore = async (p) => {
    const newVal = !p.ignored;
    await updateDoc(doc(db, 'pharmacies', pharmacy.id, 'patients', p.id), {
      ignored: newVal, updatedAt: serverTimestamp(),
      updatedBy: user.uid,
      updatedByName: user.email,
      updatedByRole: role,
    });
    await writeAuditLog(pharmacy.id, user, {
      action: newVal ? 'Patient ignored' : 'Patient unignored',
      entityType: 'patient',
      entityId: p.id,
      entityName: p.name,
      previousValue: String(p.ignored ?? false),
      newValue: String(newVal),
      actorRole: role || '',
      source: 'client',
    });
    load();
  };

  const deletePatient = async () => {
    if (!deleteConfirm) return;
    await deleteDoc(doc(db, 'pharmacies', pharmacy.id, 'patients', deleteConfirm.id));
    await writeAuditLog(pharmacy.id, user, {
      action: 'Patient deleted',
      entityType: 'patient',
      entityId: deleteConfirm.id,
      entityName: deleteConfirm.name,
      previousValue: deleteConfirm.name,
      newValue: '',
      actorRole: role || '',
      source: 'client',
    });
    setDeleteConfirm(null);
    load();
  };

  return (<>
    <div style={{ padding: '28px 32px', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 800, color: '#0F172A' }}>Dashboard</h1>
      <p style={{ margin: '0 0 24px', fontSize: 13, color: '#94A3B8' }}>
        {pharmacy?.name} · <span style={{ color: '#16A34A' }}>active</span>
      </p>

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 32, flexWrap: 'wrap' }}>
        <StatCard value={active.length} label="Active Patients" sub="total enrolled"
          color="#0F172A" selected={filter === 'all'} onClick={() => setFilter('all')} />
        <StatCard value={ready.length} label="Ready" sub="prepared blisters"
          color="#16A34A" selected={filter === 'ready'} onClick={() => setFilter('ready')} />
        <StatCard value={dueThisWeek.length} label="Due This Week" sub="pickup within 7 days"
          color="#D97706" selected={filter === 'week'} onClick={() => setFilter('week')} />
        <StatCard value={dueIn3.length} label="Due in 3 Days" sub="urgent prep window"
          color="#EA580C" selected={filter === '3days'} onClick={() => setFilter('3days')} />
        <StatCard value={overdue.length} label="Overdue" sub="needs follow-up"
          color="#DC2626" selected={filter === 'overdue'} onClick={() => setFilter('overdue')} />
      </div>

      {/* Table */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0',
        overflow: 'hidden'
      }}>
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #F1F5F9' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0F172A' }}>
            {filter === 'overdue' ? 'Overdue Patients'
              : filter === 'ready' ? 'Ready Patients'
              : filter === 'week' ? 'Due This Week'
              : filter === '3days' ? 'Due in 3 Days'
              : 'All Active Patients'}
          </h2>
        </div>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Loading…</div>
        ) : displayed.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>
            No patients in this category.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                {['Patient', 'Cycle', 'Last Pickup', 'Next Due', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{
                    padding: '10px 16px', textAlign: 'left',
                    fontSize: 11, fontWeight: 600, color: '#94A3B8',
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                    borderBottom: '1px solid #F1F5F9'
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.map((p, i) => {
                const nd = toDate(p.nextDue);
                const isOD = nd && nd < now && !p.ready;
                return (
                  <tr key={p.id} style={{ borderBottom: '1px solid #F8FAFC' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0F172A' }}>
                      {p.name}
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                      <select value={localCycles[p.id] ?? p.cycleLength ?? 28}
                        onChange={e => { setLocalCycles(prev => ({ ...prev, [p.id]: parseInt(e.target.value) })); if (p.lastPickup) saveField(p, e.target.value, null); }}
                        style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #E2E8F0', fontSize: 13, background: '#F8FAFC', cursor: 'pointer', display: 'block', margin: '0 auto' }}>
                        {[7, 14, 28, 56, 84, 168].map(v => <option key={v} value={v}>{v} days</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                      <input type="date" value={localDates[p.id] ?? fmtDate(p.lastPickup) ?? ''}
                        onChange={e => { const val = e.target.value; setLocalDates(prev => ({ ...prev, [p.id]: val })); if (val) saveField(p, localCycles[p.id] ?? p.cycleLength ?? 28, val); }}
                        onBlur={e => { const val = e.target.value; if (val) saveField(p, localCycles[p.id] ?? p.cycleLength ?? 28, val); }}
                        style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #E2E8F0', fontSize: 13, background: '#F8FAFC', cursor: 'pointer', display: 'block', margin: '0 auto' }} />
                    </td>
                    <td style={{ padding: '12px 16px', color: '#64748B', textAlign: 'center' }}>
                      {fmtDate(p.nextDue)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                        background: p.ready ? '#DCFCE7' : isOD ? '#FEE2E2' : '#FEF3C7',
                        color: p.ready ? '#16A34A' : isOD ? '#DC2626' : '#D97706'
                      }}>
                        {p.ready ? 'Ready' : isOD ? 'Overdue' : 'Prep Now'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 3 }}>
                        {canEdit && !p.ready && (
                          <ActionBtn icon={<Check size={14} weight="bold" />} title="Mark Ready" color="#16A34A" bg="#DCFCE7" onClick={() => markReady(p)} />
                        )}
                        {canEdit && (
                          <ActionBtn icon={<Package size={14} weight="bold" />} title="Mark Picked Up" color="#1768C4" bg="#DBEAFE" onClick={() => markPickedUp(p)} />
                        )}
                        {canEdit && (
                          <ActionBtn icon={<Truck size={14} weight="bold" />} title="Toggle Delivery" color={p.deliveryPatient ? '#1768C4' : '#64748B'} bg={p.deliveryPatient ? '#DBEAFE' : '#F1F5F9'} onClick={() => toggleDelivery(p)} />
                        )}
                        {canEdit && (
                          <ActionBtn icon={<NotePencil size={14} weight="bold" />} title="Add Note" color={p.note ? '#D97706' : '#64748B'} bg={p.note ? '#FEF3C7' : '#F1F5F9'} onClick={() => setNoteModal({ id: p.id, name: p.name, note: p.note || '' })} />
                        )}
                        {canEdit && (
                          <ActionBtn icon={p.ignored ? <EyeSlash size={14} weight="bold" /> : <Eye size={14} weight="bold" />} title={p.ignored ? 'Unignore' : 'Ignore'} color={p.ignored ? '#DC2626' : '#94A3B8'} bg={p.ignored ? '#FEE2E2' : '#F1F5F9'} onClick={() => ignore(p)} />
                        )}
                        {canEdit && (
                          <ActionBtn icon={<Trash size={14} weight="bold" />} title="Delete patient" color="#DC2626" bg="#FEE2E2" onClick={() => setDeleteConfirm({ id: p.id, name: p.name })} />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>

    {/* Note Modal */}
    {noteModal && (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
      }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>Patient Note</h3>
          <p style={{ margin: '0 0 16px', fontSize: 12, color: '#64748B' }}>{noteModal.name}</p>
          <textarea
            value={noteModal.note}
            onChange={e => setNoteModal({ ...noteModal, note: e.target.value })}
            placeholder="Add a note about this patient..."
            style={{ width: '100%', minHeight: 100, padding: '10px 12px', borderRadius: 8,
              border: '1px solid #E2E8F0', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
            <button onClick={() => setNoteModal(null)} style={{
              padding: '8px 16px', borderRadius: 8, border: '1px solid #E2E8F0',
              background: '#F8FAFC', color: '#64748B', fontSize: 13, cursor: 'pointer'
            }}>Cancel</button>
            <button onClick={saveNote} style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: '#1768C4', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600
            }}>Save Note</button>
          </div>
        </div>
      </div>
    )}
    {deleteConfirm && (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
      }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#0F172A' }}>Delete Patient</h3>
          <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748B' }}>
            Are you sure you want to permanently delete <strong>{deleteConfirm.name}</strong>? This cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setDeleteConfirm(null)} style={{
              padding: '8px 16px', borderRadius: 8, border: '1px solid #E2E8F0',
              background: '#F8FAFC', color: '#64748B', fontSize: 13, cursor: 'pointer'
            }}>Cancel</button>
            <button onClick={deletePatient} style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: '#DC2626', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600
            }}>Delete</button>
          </div>
        </div>
      </div>
    )}
  </>);
}
